import { PendingCountBadge } from '@/components/Nav/PendingCountBadge';
import type { WebSocketUnreadCounterProps } from '@/components/UI/WebSocketUnreadCounter';
import WebSocketUnreadCounter from '@/components/UI/WebSocketUnreadCounter';
import { Suspense } from 'react';
import { twMerge } from 'tailwind-merge';

type Color = 'default' | 'primary' | 'secondary' | 'red' | 'yellow';

// @TODO biome-ignore lint/suspicious/noExplicitAny:
export interface Link<R = any> {
  href: string;
  startsWithHref?: string;
  // Set for external destinations (e.g. affiliate offers) so the link opens in a
  // new tab / the system browser on mobile instead of navigating away from the app.
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  icon?: React.ForwardRefExoticComponent<
    React.PropsWithoutRef<React.SVGProps<SVGSVGElement>> & {
      title?: string;
      titleId?: string;
    } & React.RefAttributes<SVGSVGElement>
  >;
  sub?: string;
  text: string;
  count?: WebSocketUnreadCounterProps<R>;
  // A live badge rendered next to the label (e.g. 'pending' -> N pending plays).
  badge?: 'pending';
  roleRequired?: boolean;
}

export const NavLink = ({
  color = 'default',
  link,
  urlParsed,
}: {
  color?: Color;
  link: Link;
  urlParsed: { pathnameOriginal: string | undefined };
}) => {
  const Component = link.count ? WebSocketUnreadCounter : 'a';
  const IconComponent = link.icon;

  return (
    <Suspense
      key={link.href + link.text}
      fallback={
        <a
          href={link.href}
          target={link.target}
          rel={link.rel}
          data-active={
            urlParsed.pathnameOriginal === link.href ||
            (link.startsWithHref && urlParsed.pathnameOriginal?.startsWith?.(link.startsWithHref))
          }
          className={twMerge(
            'flex w-full items-center gap-x-3 rounded-sm px-3 py-2 font-semibold text-zinc-400 leading-6 transition-[width,color,background-color] duration-500 ease-out hover:bg-white/5 hover:duration-75 active:bg-white/5 data-[active=true]:bg-white/10',
            color === 'default' &&
              'text-zinc-400 hover:text-white active:text-white data-[active=true]:text-white',
            color === 'primary' &&
              'text-emerald-400 hover:text-emerald-300 active:text-emerald-300 data-[active=true]:text-emerald-300',
            color === 'secondary' &&
              'text-blue-400 hover:text-blue-300 active:text-blue-300 data-[active=true]:text-blue-300',
            color === 'red' &&
              'text-red-400 hover:text-red-300 active:text-red-300 data-[active=true]:text-red-300',
            color === 'yellow' &&
              'text-yellow-400 hover:text-yellow-300 active:text-yellow-300 data-[active=true]:text-yellow-300',
          )}
        >
          {!!IconComponent && (
            <IconComponent className="size-6 shrink-0" width={24} aria-hidden="true" />
          )}
          <div className="flex grow flex-row items-baseline justify-between">
            <span className="flex-1 whitespace-nowrap">{link.text}</span>
            {!!link.sub && (
              <span className="inline-flex items-center justify-center rounded-full bg-white/20 px-2 py-0.5 font-medium text-white text-xs">
                {link.sub}
              </span>
            )}
          </div>
        </a>
      }
    >
      <Component
        href={link.href}
        target={link.target}
        rel={link.rel}
        {...(link.count as WebSocketUnreadCounterProps)}
        data-active={
          urlParsed.pathnameOriginal === link.href ||
          (link.startsWithHref && urlParsed.pathnameOriginal?.startsWith?.(link.startsWithHref))
        }
        className={twMerge(
          'flex w-full items-center gap-x-3 rounded px-3 py-2 font-semibold text-zinc-400 leading-6 transition-[width,color,background-color] duration-500 ease-out hover:bg-white/5 hover:duration-75 active:bg-white/5 data-[active=true]:bg-white/10',
          color === 'default' &&
            'text-zinc-400 hover:text-white active:text-white data-[active=true]:text-white',
          color === 'primary' &&
            'text-emerald-400 hover:text-emerald-300 active:text-emerald-300 data-[active=true]:text-emerald-300',
          color === 'secondary' &&
            'text-blue-400 hover:text-blue-300 active:text-blue-300 data-[active=true]:text-blue-300',
          color === 'red' &&
            'text-red-400 hover:text-red-300 active:text-red-300 data-[active=true]:text-red-300',
          color === 'yellow' &&
            'text-yellow-400 hover:text-yellow-300 active:text-yellow-300 data-[active=true]:text-yellow-300',
        )}
      >
        {!!IconComponent && (
          <IconComponent className="size-6 shrink-0" width={24} aria-hidden="true" />
        )}
        <div className="flex grow flex-row items-center justify-between">
          <span className="flex-1 whitespace-nowrap">{link.text}</span>
          {link.badge === 'pending' && (
            <Suspense fallback={null}>
              <PendingCountBadge />
            </Suspense>
          )}
          {!!link.sub && (
            <span className="inline-flex items-center justify-center rounded-full bg-white/20 px-2 py-0.5 font-medium text-white text-xs">
              {link.sub}
            </span>
          )}
        </div>
      </Component>
    </Suspense>
  );
};

export default NavLink;
